// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

import * as React from 'react';

import { fromUint8Array, toUint8Array } from 'js-base64';

import { baseURL, defined } from '@simlin/core/common';
import { first } from '@simlin/core/collections';

import type { Tier } from '@simlin/core/tier';

import { Editor, ProtobufProjectData } from './Editor';
import { ViewMode } from './BuildLoopsPlaySwitch';
import type { ProposeFn, DescribeFn } from './agent-conversation';

import styles from './HostedWebEditor.module.css';

class HostedWebEditorError implements Error {
  name = 'HostedWebEditorError';
  message: string;
  constructor(msg: string) {
    this.message = msg;
  }
}

interface HostedWebEditorProps {
  username: string;
  projectName: string;
  embedded?: boolean;
  baseURL?: string;
  readOnlyMode?: boolean;
  // Optional dashboard-navigation callback forwarded to the inner
  // Editor. Wired by @simlin/app via wouter's setLocation('/'); when
  // omitted (e.g., used as a standalone embed) the Home button is
  // not rendered.
  onHome?: () => void;
  // CO-3: local-first editor preferences from the host's Settings dialog,
  // forwarded to the Editor. Omitted -> the Editor's built-in defaults.
  initialViewMode?: ViewMode;
  showCanvasGrid?: boolean;
  canvasPaper?: 'white' | 'yellow' | 'green' | 'black';
  // WS4: the Merlin proposer + Athena describer + the user's tier, forwarded to
  // the Editor so the Agents tab is live in the hosted app. Omitted -> off.
  propose?: ProposeFn;
  describe?: DescribeFn;
  agentTier?: Tier;
}

interface HostedWebEditorState {
  serviceErrors: readonly Error[];
  projectBinary: Readonly<Uint8Array> | undefined;
  projectVersion: number;
}

export class HostedWebEditor extends React.PureComponent<HostedWebEditorProps, HostedWebEditorState> {
  // Tracks whether the component is currently mounted so the async
  // loadProject fetch can skip its setState call when the component
  // unmounts mid-flight (e.g., StrictMode's dev double-mount, or the
  // user navigating away before the fetch resolves).
  private _isMounted = false;

  constructor(props: HostedWebEditorProps) {
    super(props);

    this.state = {
      serviceErrors: [],
      projectBinary: undefined,
      projectVersion: -1,
    };
  }

  componentDidMount(): void {
    // Side effects (the initial project fetch) run after mount, not
    // from the constructor.  Previously a setTimeout in the constructor
    // could resolve before React committed the mount, causing setState
    // to fire on an unmounted component ("Can't call setState on a
    // component that is not yet mounted").
    this._isMounted = true;
    void this.loadProject();
  }

  componentWillUnmount(): void {
    this._isMounted = false;
  }

  appendModelError(msg: string) {
    if (!this._isMounted) {
      return;
    }
    this.setState({
      serviceErrors: [...this.state.serviceErrors, new HostedWebEditorError(msg)],
    });
  }

  getBaseURL(): string {
    return this.props.baseURL ?? baseURL;
  }

  handleSave = async (project: ProtobufProjectData, currVersion: number): Promise<number | undefined> => {
    if (this.props.readOnlyMode) return;

    const bodyContents = {
      currVersion,
      projectPB: fromUint8Array(project.data as Uint8Array),
    };

    const base = this.getBaseURL();
    const apiPath = `${base}/api/projects/${this.props.username}/${this.props.projectName}`;
    const response = await fetch(apiPath, {
      credentials: 'same-origin',
      method: 'POST',
      cache: 'no-cache',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(bodyContents),
    });

    const status = response.status;
    if (!(status >= 200 && status < 400)) {
      const body = await response.json();
      const errorMsg =
        body && body.error ? (body.error as string) : `HTTP ${status}; maybe try a different username ¯\\_(ツ)_/¯`;
      this.appendModelError(errorMsg);
      return undefined;
    }

    const projectResponse = await response.json();
    const projectVersion = defined(projectResponse.version) as number;

    this.setState({ projectVersion });

    return projectVersion;
  };

  async loadProject(): Promise<void> {
    const base = this.getBaseURL();
    const apiPath = `${base}/api/projects/${this.props.username}/${this.props.projectName}`;
    const response = await fetch(apiPath);
    if (response.status >= 400) {
      this.appendModelError(`unable to load ${apiPath}`);
      return;
    }

    const projectResponse = await response.json();

    const projectBinary = toUint8Array(projectResponse.pb);

    if (!this._isMounted) {
      return;
    }
    this.setState({
      projectBinary,
      projectVersion: defined(projectResponse.version) as number,
    });
  }

  render(): React.ReactNode {
    const { embedded } = this.props;

    if (!this.state.projectBinary || !this.state.projectVersion) {
      if (this.state.serviceErrors.length > 0) {
        return <div>{first(this.state.serviceErrors).message}</div>;
      } else {
        return <div />;
      }
    }

    const classNames = embedded ? undefined : styles.bg;

    return (
      <div className={classNames}>
        <Editor
          inputFormat="protobuf"
          initialProjectBinary={this.state.projectBinary}
          initialProjectVersion={this.state.projectVersion}
          name={this.props.projectName}
          embedded={this.props.embedded}
          onSave={this.handleSave}
          readOnlyMode={this.props.readOnlyMode}
          onHome={this.props.onHome}
          initialViewMode={this.props.initialViewMode}
          showCanvasGrid={this.props.showCanvasGrid}
          canvasPaper={this.props.canvasPaper}
          propose={this.props.propose}
          describe={this.props.describe}
          agentTier={this.props.agentTier}
        />
      </div>
    );
  }
}
